import React, { useState, useRef, useEffect } from 'react';

interface MonthYearValue {
  startDate: string | null;
  endDate: string | null;
}

interface MonthYearRangePickerProps {
  value: MonthYearValue;
  onChange: (value: MonthYearValue) => void;
  startLabel?: string;
  disabled?: boolean;
}

const MONTHS = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

export const MonthYearRangePicker: React.FC<MonthYearRangePickerProps> = ({
  value,
  onChange,
  startLabel = 'Месяц',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectingField, setSelectingField] = useState<'start' | 'end'>('start');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const formatMonthYearForDisplay = (dateStr: string | null) => {
    if (!dateStr) return '';
    try {
      const [year, month] = dateStr.split('-');
      return `${MONTHS[parseInt(month) - 1]} ${year}`;
    } catch {
      return dateStr;
    }
  };

  const displayValue = () => {
    if (value.startDate && value.endDate) {
      return `${formatMonthYearForDisplay(value.startDate)} - ${formatMonthYearForDisplay(value.endDate)}`;
    } else if (value.startDate) {
      return formatMonthYearForDisplay(value.startDate);
    } else if (value.endDate) {
      return formatMonthYearForDisplay(value.endDate);
    }
    return '';
  };

  const handleMonthClick = (monthIndex: number) => {
    const monthStr = String(monthIndex + 1).padStart(2, '0');
    const dateValue = `${currentYear}-${monthStr}`;

    if (selectingField === 'start') {
      onChange({
        startDate: dateValue,
        endDate: value.endDate
      });
      setSelectingField('end');
    } else {
      onChange({
        startDate: value.startDate,
        endDate: dateValue
      });
      setIsOpen(false);
    }
  };

  const handleYearChange = (direction: 'prev' | 'next') => {
    setCurrentYear(prev => direction === 'prev' ? prev - 1 : prev + 1);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        if (!value.startDate) {
          setSelectingField('start');
        } else if (!value.endDate) {
          setSelectingField('end');
        } else {
          setSelectingField('start');
        }

        if (selectingField === 'start' && value.startDate) {
          setCurrentYear(parseInt(value.startDate.split('-')[0]));
        } else if (selectingField === 'end' && value.endDate) {
          setCurrentYear(parseInt(value.endDate.split('-')[0]));
        }
      }
    }
  };

  const isMonthSelected = (monthIndex: number) => {
    const monthStr = String(monthIndex + 1).padStart(2, '0');
    const dateValue = `${currentYear}-${monthStr}`;
    return value.startDate === dateValue || value.endDate === dateValue;
  };

  const isMonthDisabled = (monthIndex: number) => {
    if (selectingField === 'end' && value.startDate) {
      const monthStr = String(monthIndex + 1).padStart(2, '0');
      const dateValue = `${currentYear}-${monthStr}`;
      return dateValue < value.startDate;
    }
    return false;
  };

  return (
    <div className='px-5 pb-5'>
      <div className='mb-2 relative'>
        <label className='block text-sm text-gray-700 mb-2'>{startLabel}</label>

        <div
          ref={inputRef}
          className={`relative ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          onClick={toggleDropdown}
        >
          <input
            type="text"
            value={displayValue()}
            readOnly
            placeholder="Выберите период"
            className={`cursor-pointer text-left block w-full text-base rounded-md border border-[#DFE4EA] py-3 px-5 text-black outline-none shadow-custom ${
              disabled ? 'cursor-not-allowed text-[#6B7280] bg-[#E5E7EB]' : ''
            }`}
            disabled={disabled}
          />
          <div className="absolute right-2 top-0 h-full flex items-center px-3 text-[#2563EB] pointer-events-none">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 2C6 1.44772 6.44772 1 7 1C7.55228 1 8 1.44772 8 2V3H12V2C12 1.44772 12.4477 1 13 1C13.5523 1 14 1.44772 14 2V3H16C17.1046 3 18 3.89543 18 5V17C18 18.1046 17.1046 19 16 19H4C2.89543 19 2 18.1046 2 17V5C2 3.89543 2.89543 3 4 3H6V2ZM6 5H4V7H16V5H14V6C14 6.55228 13.5523 7 13 7C12.4477 7 12 6.55228 12 6V5H8V6C8 6.55228 7.55228 7 7 7C6.44772 7 6 6.55228 6 6V5ZM16 9H4V17H16V9Z"/>
            </svg>
          </div>
        </div>

        {isOpen && !disabled && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 mt-1 bg-white border border-[#DFE4EA] rounded-lg shadow-lg z-50 p-4 w-full"
            style={{ minWidth: '280px' }}
          >
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => handleYearChange('prev')}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L6 10L12 16" stroke="#637381" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="flex gap-2">
                {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                  <button
                    key={year}
                    onClick={() => setCurrentYear(year)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                      year === currentYear
                        ? 'bg-[#2563EB] text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handleYearChange('next')}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 4L14 10L8 16" stroke="#637381" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {MONTHS.map((month, index) => {
                const selected = isMonthSelected(index);
                const disabled = isMonthDisabled(index);

                return (
                  <button
                    key={index}
                    onClick={() => !disabled && handleMonthClick(index)}
                    disabled={disabled}
                    className={`py-2 px-3 rounded text-sm transition-colors ${
                      selected
                        ? 'bg-[#2563EB] text-white font-medium'
                        : disabled
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {month}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
